import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

// Ad types: 'pre_roll', 'mid_roll'
export const AdPlayer = ({ 
  type = 'pre_roll', 
  duration = 15, 
  onComplete, 
  onSkip,
  filmId,
  skippable = true,
  skipAfter = 5
}) => {
  const [timeRemaining, setTimeRemaining] = useState(duration);
  const [canSkip, setCanSkip] = useState(false);
  const adContainerRef = useRef(null);
  const scriptRef = useRef(null);

  // Inject Adsterra native ad script
  useEffect(() => {
    if (!adContainerRef.current) return;

    const script = document.createElement('script');
    script.src = 'https://pl28954329.profitablecpmratenetwork.com/fa804567fda6371ac31437c91d64ac97/invoke.js';
    script.async = true;
    script.setAttribute('data-cfasync', 'false');
    scriptRef.current = script;
    adContainerRef.current.appendChild(script);

    return () => {
      // Cleanup: remove the script when component unmounts
      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }
    };
  }, []);

  // Countdown timer
  useEffect(() => {
    if (timeRemaining <= 0) {
      onComplete?.();
      return;
    }

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, onComplete]);

  // Enable skip after delay
  useEffect(() => {
    if (!skippable) return;

    const skipTimer = setTimeout(() => {
      setCanSkip(true);
    }, skipAfter * 1000);

    return () => clearTimeout(skipTimer);
  }, [skippable, skipAfter]);

  const handleSkip = () => {
    onSkip?.();
    onComplete?.();
  };

  return (
    <div className="absolute inset-0 z-50 bg-black flex flex-col items-center justify-center" data-testid={`ad-${type}`}>
      <div className="relative w-full max-w-3xl mx-auto px-4">
        {/* Ad Label */}
        <div className="absolute top-4 left-4 z-10 flex items-center gap-2">
          <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-bold rounded">AD</span>
        </div>

        {/* Skip Button */}
        {skippable && (
          <div className="absolute top-4 right-4 z-10">
            {canSkip ? (
              <button
                onClick={handleSkip}
                className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white text-sm font-medium rounded transition-colors flex items-center gap-2"
                data-testid="skip-ad-button"
              >
                Skip Ad <X className="w-4 h-4" />
              </button>
            ) : (
              <div className="px-4 py-2 bg-black/50 text-white/60 text-sm rounded">
                Skip in {skipAfter - (duration - timeRemaining)}s
              </div>
            )}
          </div>
        )}

        {/* Adsterra Native Ad Container */}
        <div className="rounded-lg overflow-hidden bg-[#0F0B1E] min-h-[250px] flex items-center justify-center" data-testid="ad-native-wrapper">
          <div ref={adContainerRef}>
            <div id="container-fa804567fda6371ac31437c91d64ac97"></div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-4 w-full">
          <div className="flex justify-between text-sm text-white/60 mb-2">
            <span>Video starts in {timeRemaining}s</span>
            <span>{type === 'pre_roll' ? 'Pre-roll' : 'Mid-roll'} Ad</span>
          </div>
          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#00FF9D] transition-all duration-1000" 
              style={{ width: `${((duration - timeRemaining) / duration) * 100}%` }} 
            />
          </div>
        </div>

        {/* Premium upsell */}
        <div className="mt-4 text-center">
          <p className="text-white/40 text-sm">
            Tired of ads? <a href="/pricing" className="text-[#8B5CF6] hover:underline">Get Premium for $3.99/mo</a>
          </p>
        </div>
      </div>
    </div>
  );
};

// Video Overlay Ad (small ad that appears during video)
export const OverlayAd = ({ filmId, onClose }) => {
  const [ad, setAd] = useState(null);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const adData = await adsApi.getAd('overlay', filmId);
        setAd(adData);
        
        // Auto-close after 10 seconds
        const timer = setTimeout(() => {
          onClose?.();
        }, 10000);
        
        return () => clearTimeout(timer);
      } catch (error) {
        onClose?.();
      }
    };
    fetchAd();
  }, [filmId, onClose]);

  const handleClick = async () => {
    if (ad?.click_url) {
      if (ad?.ad_id) {
        await adsApi.trackClick(ad.ad_id, filmId).catch(() => {});
      }
      window.open(ad.click_url, '_blank');
    }
  };

  if (!ad) return null;

  return (
    <div 
      className="absolute bottom-20 left-4 z-40 max-w-xs bg-black/90 rounded-lg overflow-hidden border border-white/10 shadow-xl cursor-pointer"
      onClick={handleClick}
      data-testid="overlay-ad"
    >
      <div className="flex items-center gap-3 p-3">
        <span className="px-1.5 py-0.5 bg-yellow-500 text-black text-[10px] font-bold rounded">AD</span>
        {ad.image_url && (
          <img src={ad.image_url} alt="" className="w-12 h-12 rounded object-cover" />
        )}
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-medium truncate">{ad.title}</p>
          <p className="text-white/60 text-xs truncate">{ad.description}</p>
        </div>
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onClose?.();
          }}
          className="p-1 hover:bg-white/10 rounded transition-colors"
        >
          <X className="w-4 h-4 text-white/40" />
        </button>
      </div>
    </div>
  );
};

export default AdPlayer;
